import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';

/**
 * Landing page after Mollie Checkout completes.
 *
 * URL: /billing/success?session_id=cs_test_...
 *
 * Mollie redirects here on a successful or trial checkout. We poll
 * the backend until the subscription state propagates (the webhook
 * does the actual heavy lifting — this page is just a UX bridge).
 */
function BillingSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Accept either query-param name — legacy Stripe redirects used
  // `session_id` and early Mollie builds used `payment_id`. Also fall
  // back to the localStorage value stashed by Pricing.js so a checkout
  // succeeds even if Mollie's redirectUrl couldn't be patched (see
  // mollie_billing_service.create_checkout_session).
  const urlSessionId = params.get('session_id') || params.get('payment_id');
  const ignoreUrl = !urlSessionId || urlSessionId === '{id}' || urlSessionId.includes('{');
  const fallbackSessionId = (() => {
    try { return localStorage.getItem('kw_last_billing_session') || null; } catch { return null; }
  })();
  const sessionId = ignoreUrl ? fallbackSessionId : urlSessionId;
  const [state, setState] = useState({ loading: true, status: null, error: null });

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    async function poll() {
      try {
        const data = await api.getBillingCheckoutStatus(sessionId);
        if (cancelled) return;
        setState({ loading: false, status: data, error: null });
        // If still incomplete, give the webhook another moment to apply.
        if (data.status !== 'complete' && attempts < 5) {
          attempts += 1;
          setTimeout(poll, 1800);
        }
      } catch (err) {
        if (!cancelled) setState({ loading: false, status: null, error: err.message });
      }
    }
    if (sessionId) poll();
    else setState({ loading: false, status: null, error: 'Missing session id' });
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main data-testid="billing-success-page" style={page}>
      <div style={card}>
        <div style={icon}>✨</div>
        <h1 style={h1}>You're all set!</h1>
        {state.loading && (
          <p style={p} data-testid="billing-success-loading">
            Confirming your subscription…
          </p>
        )}
        {!state.loading && state.error && (
          <p style={{ ...p, color: '#c45353' }} data-testid="billing-success-error">
            We couldn't confirm your subscription right away — it may still
            be processing. Please open the Billing tab in a moment to confirm.
            <br /><small>{state.error}</small>
          </p>
        )}
        {!state.loading && state.status && (
          <p style={p} data-testid="billing-success-ok">
            {state.status.payment_status === 'paid'
              ? 'Payment received — your plan is active.'
              : 'Your free trial has started. We\'ll only charge you when it ends, and you can cancel anytime.'}
          </p>
        )}
        <div style={actions}>
          <button
            type="button"
            onClick={() => navigate('/?tab=billing')}
            style={primaryBtn}
            data-testid="billing-success-go-billing"
          >
            Open Billing
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            style={secondaryBtn}
            data-testid="billing-success-go-home"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </main>
  );
}

const page = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, hsl(189 53% 25%) 0%, hsl(189 60% 14%) 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem 1rem',
};
const card = {
  background: '#fff',
  borderRadius: 18,
  padding: '2.4rem 2rem',
  maxWidth: 480,
  width: '100%',
  textAlign: 'center',
  boxShadow: '0 30px 60px rgba(0,0,0,0.35)',
};
const icon = { fontSize: '2.6rem', marginBottom: '0.5rem' };
const h1 = { fontSize: '1.7rem', fontWeight: 700, color: '#0b3d3a', margin: '0 0 0.6rem 0' };
const p = { color: '#446f6b', margin: '0 0 1.5rem 0', lineHeight: 1.55 };
const actions = { display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' };
const primaryBtn = {
  padding: '0.65rem 1.2rem',
  borderRadius: 10,
  background: 'hsl(189 53% 25%)',
  color: '#fff',
  border: 'none',
  fontWeight: 700,
  cursor: 'pointer',
};
const secondaryBtn = {
  padding: '0.65rem 1.2rem',
  borderRadius: 10,
  background: '#fff',
  color: 'hsl(189 53% 25%)',
  border: '1px solid hsl(189 53% 25%)',
  fontWeight: 700,
  cursor: 'pointer',
};

export default BillingSuccess;
